import { useEffect, useRef, useState } from 'react';
import { KnowledgeFileData } from '../interfaces/Knowledge';
import { ResponseObject } from '../interfaces/App';
import Knowledge, { KnowledgeFileMap } from '../interfaces/Knowledge';
import { getFileDownloadUrl } from '../services/knowledgeService';
import {
  buildKnowledgeFileCacheKey,
  indexDBService,
} from '../services/indexDBService';
import { deleteKnowledgeFiles } from '../helpers/knowledgeHelper';
import { useSnackBar } from './useSnackBar';

// How long to wait, after the last change to knowledgeFilesData, before
// persisting it to IndexedDB. Long enough to coalesce a bulk multi-file
// FilePond selection into a single write instead of one per file.
const KNOWLEDGE_FILES_SAVE_DEBOUNCE_MS = 500;

const useKnowledgeFile = (knowledge: Knowledge, cacheScope: string) => {
  const [knowledgeFiles, setKnowledgeFiles] = useState<KnowledgeFileMap>({});
  const [isKnowledgeFilesLoading, setIsKnowledgeFilesLoading] = useState(false);

  const snackBar = useSnackBar();

  const [knowledgeFilesData, setKnowledgeFilesData] = useState<
    KnowledgeFileData[]
  >([]);

  const [pendingDeletions, setPendingDeletions] = useState<string[]>([]);
  const hasLoadedPendingDeletionsRef = useRef(false);

  const cacheKey = buildKnowledgeFileCacheKey(cacheScope, knowledge?.id);

  useEffect(() => {
    setKnowledgeFiles(knowledge?.knowledge_files as KnowledgeFileMap);
  }, [knowledge]);

  useEffect(() => {
    const loadFromIndexDB = async () => {
      try {
        const files = await indexDBService.getKnowledgeFiles(cacheKey);
        if (files.length > 0) {
          setKnowledgeFilesData(files);
        }
      } catch (error) {
        console.warn('Failed to load knowledge files from IndexDB:', error);
      }
    };

    loadFromIndexDB();
  }, [cacheKey]);

  useEffect(() => {
    if (knowledgeFilesData.length === 0) return;

    // Debounced: a bulk FilePond selection fires one onaddfile per file in
    // quick succession, each appending to knowledgeFilesData. Without this,
    // every single append re-serialized and wrote the WHOLE accumulated
    // array (all prior files' base64 content) to IndexedDB again - O(n)
    // writes of O(n) size each, i.e. O(n^2) bytes on the main thread for a
    // batch of n files. At 100+ files (each up to 20MB, ~37% larger
    // base64-encoded) that blew up memory/main-thread time enough to freeze
    // or crash the tab. Debouncing collapses a burst into a single write of
    // the settled array.
    const timeoutId = setTimeout(() => {
      indexDBService
        .saveKnowledgeFiles(cacheKey, knowledgeFilesData)
        .catch(error => {
          console.warn('Failed to save knowledge files to IndexDB:', error);
        });
    }, KNOWLEDGE_FILES_SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [cacheKey, knowledgeFilesData]);

  useEffect(() => {
    hasLoadedPendingDeletionsRef.current = false;

    const loadPendingDeletions = async () => {
      try {
        const deletions = await indexDBService.getPendingDeletions(cacheKey);
        setPendingDeletions(deletions);
      } catch (error) {
        console.warn('Failed to load pending deletions from IndexDB:', error);
      } finally {
        hasLoadedPendingDeletionsRef.current = true;
      }
    };

    loadPendingDeletions();
  }, [cacheKey]);

  useEffect(() => {
    // Skip the initial (pre-load) render: without this guard, this effect
    // would fire with the empty starting state before the load effect above
    // has resolved, overwriting whatever was already persisted for this key.
    if (!hasLoadedPendingDeletionsRef.current) return;

    const savePendingDeletionsToIndexDB = async () => {
      try {
        await indexDBService.savePendingDeletions(cacheKey, pendingDeletions);
      } catch (error) {
        console.warn('Failed to save pending deletions to IndexDB:', error);
      }
    };

    savePendingDeletionsToIndexDB();
  }, [cacheKey, pendingDeletions]);

  const markKnowledgeFileForDeletion = (knowledgeFileName: string) => {
    setPendingDeletions(prev =>
      prev.includes(knowledgeFileName) ? prev : [...prev, knowledgeFileName]
    );
  };

  const unmarkKnowledgeFileForDeletion = (knowledgeFileName: string) => {
    setPendingDeletions(prev =>
      prev.filter(fileName => fileName !== knowledgeFileName)
    );
  };

  // Sends every currently pending deletion in parallel. Successfully
  // deleted files are dropped from both pendingDeletions and knowledgeFiles
  // (so they don't reappear); failed ones are dropped from pendingDeletions
  // only, so they fall back to showing normally (their mark is cleared, not
  // retried) - the caller uses the returned failed list to warn the user.
  //
  // A file gets marked for deletion when the user drops a same-named
  // replacement (AgentKnowledgeStep marks the old entry, stages the new
  // content in knowledgeFilesData). The caller uploads that replacement in
  // parallel with this call, and the backend upload upsert already
  // overwrites that filename's blob and Cosmos entry - so also sending a
  // real delete for it here would race the upload (whichever request lands
  // last wins, silently destroying either the new content or the entry).
  // Filtering those names out here, once, keeps every call site correct
  // without each of them having to know about the replace-vs-delete
  // distinction.
  const executeStagedDeletions = async (): Promise<{ failed: string[] }> => {
    const stagedForReupload = new Set(
      knowledgeFilesData.map(fileData => fileData.name)
    );
    const attemptedFileNames = pendingDeletions.filter(
      fileName => !stagedForReupload.has(fileName)
    );
    if (attemptedFileNames.length === 0) {
      // Marks superseded by a same-named replacement still need clearing
      // even with nothing left to actually delete, or they'd linger as
      // "pending deletion" forever (the early return below would otherwise
      // skip the cleanup that normally happens after the delete call).
      if (pendingDeletions.some(fileName => stagedForReupload.has(fileName))) {
        setPendingDeletions(prev =>
          prev.filter(fileName => !stagedForReupload.has(fileName))
        );
      }
      return { failed: [] };
    }

    const results = await deleteKnowledgeFiles(
      attemptedFileNames,
      knowledge?.id as string
    );

    const succeeded: string[] = [];
    const failed: string[] = [];
    results.forEach((result, index) => {
      const fileName = attemptedFileNames[index];
      if (result.status === 'fulfilled' && result.value?.status === 'success') {
        succeeded.push(fileName);
      } else {
        failed.push(fileName);
      }
    });

    if (succeeded.length > 0) {
      setKnowledgeFiles(prev =>
        Object.fromEntries(
          Object.entries(prev).filter(([key]) => !succeeded.includes(key))
        )
      );
    }

    setPendingDeletions(prev =>
      prev.filter(
        fileName =>
          !attemptedFileNames.includes(fileName) &&
          !stagedForReupload.has(fileName)
      )
    );

    return { failed };
  };

  const handleDownloadKnowledgeFile = async (knowledgeFileName: string) => {
    setIsKnowledgeFilesLoading(true);
    try {
      const result: ResponseObject = await getFileDownloadUrl(
        knowledge?.id as string,
        knowledgeFileName
      );
      if (result?.status === 'success') {
        window.open(result.message.download_url, '_blank');
      } else {
        snackBar(result?.message, 'error');
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        snackBar(err.message, 'error');
      }
    } finally {
      setIsKnowledgeFilesLoading(false);
    }
  };

  const clearIndexDBFiles = async () => {
    try {
      await indexDBService.clearKnowledgeFiles(cacheKey);
      setKnowledgeFilesData([]);
      setPendingDeletions([]);
    } catch (error) {
      console.warn('Failed to clear knowledge files from IndexDB:', error);
    }
  };

  const clearKnowledgeFilesData = () => {
    setKnowledgeFilesData([]);
  };

  return {
    isKnowledgeFilesLoading,
    knowledgeFiles,
    knowledgeFilesData,
    pendingDeletions,
    executeStagedDeletions,
    handleDownloadKnowledgeFile,
    markKnowledgeFileForDeletion,
    unmarkKnowledgeFileForDeletion,
    setIsKnowledgeFilesLoading,
    setKnowledgeFiles,
    setKnowledgeFilesData,
    clearIndexDBFiles,
    clearKnowledgeFilesData,
  };
};

export default useKnowledgeFile;
